import React from "react";

export default function AdminWalletTable({
  scope = "transactions", // "transactions" | "transfers"
  loading,
  err,
  items,
  onOpen,
  onApprove,
  onReject,
  nextOffset,
  onLoadMore,
}) {
  const title = scope === "transactions" ? "Transactions" : "Transferts";

  return (
    <section className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-900 shadow-sm p-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
            {title}
          </h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Cliquez sur une ligne pour voir les détails.
          </p>
        </div>
      </div>

      {loading && <Skeleton />}

      {!loading && err && (
        <div className="mt-4 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {err}
        </div>
      )}

      {!loading && !err && (items?.length || 0) === 0 && (
        <div className="mt-4 rounded-2xl border border-dashed border-black/10 dark:border-white/10 p-5 text-center">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {scope === "transactions" ? "Aucune transaction" : "Aucun transfert"}
          </p>
        </div>
      )}

      {!loading && !err && (items?.length || 0) > 0 && (
        <div className="mt-4 space-y-2">
          {items.map((tx) => (
            <Row
              key={tx.id || tx.reference}
              scope={scope}
              tx={tx}
              onOpen={() => onOpen(tx)}
              onApprove={() => onApprove(tx)}
              onReject={() => onReject(tx)}
            />
          ))}

          {nextOffset !== null && (
            <button
              onClick={onLoadMore}
              className="mt-3 w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-900 px-4 py-3 text-sm font-medium
                         text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
            >
              Charger plus
            </button>
          )}
        </div>
      )}
    </section>
  );
}

function Row({ scope, tx, onOpen, onApprove, onReject }) {
  const statusPill = pill(tx.status);
  const ref = tx.reference || "—";
  const created = tx.createdAt ? new Date(tx.createdAt).toLocaleString() : "—";

  if (scope === "transactions") {
    return (
      <button
        onClick={onOpen}
        className="w-full text-left rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-4 py-3 hover:shadow-sm transition"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-slate-900 dark:text-white">
                {prettyType(tx.type)}
              </span>
              <span className={statusPill.cls}>{statusPill.label}</span>
            </div>

            <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 break-words">
              Ref:{" "}
              <b className="text-slate-700 dark:text-slate-200">{ref}</b> • {created}
            </div>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <MetaBlock
                label="Utilisateur"
                primary={tx.user?.fullName || "—"}
                secondary={[
                  tx.user?.email || null,
                  tx.user?.phone || null,
                  tx.user?.id != null ? `User ID: ${tx.user.id}` : null,
                ]}
              />

              <MetaBlock
                label="Wallet"
                primary={tx.wallet?.id != null ? `Wallet #${tx.wallet.id}` : "—"}
                secondary={[
                  tx.wallet?.balance != null
                    ? `Solde: ${fmtMoney(tx.wallet.balance, tx.wallet.currency || "CAD")}`
                    : null,
                  tx.wallet?.heldBalance != null
                    ? `Hold: ${fmtMoney(tx.wallet.heldBalance, tx.wallet.currency || "CAD")}`
                    : null,
                  tx.method || null,
                ]}
              />
            </div>

            {(tx.note || tx.metadata?.localCurrency || tx.metadata?.localAmount) && (
              <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 break-words">
                {tx.note ? (
                  <>
                    Note:{" "}
                    <span className="text-slate-700 dark:text-slate-200">{tx.note}</span>
                  </>
                ) : null}

                {tx.metadata?.localCurrency || tx.metadata?.localAmount ? (
                  <span className={tx.note ? " ml-2" : ""}>
                    • Local:{" "}
                    <span className="text-slate-700 dark:text-slate-200">
                      {tx.metadata?.localAmount ?? "—"} {tx.metadata?.localCurrency ?? ""}
                    </span>
                  </span>
                ) : null}
              </div>
            )}
          </div>

          <div className="text-right shrink-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">
              {fmtMoney(tx.amount, tx.currency || "CAD")}
            </div>

            {tx.feeAmount != null && Number(tx.feeAmount || 0) > 0 && (
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Fee: {fmtMoney(tx.feeAmount, tx.feeCurrency || "CAD")}
              </div>
            )}

            <div className="mt-2 flex items-center justify-end gap-2">
              {tx.status === "pending" && (
                <>
                  <SmallBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      onReject();
                    }}
                    variant="ghost"
                  >
                    Rejeter
                  </SmallBtn>
                  <SmallBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      onApprove();
                    }}
                    variant="primary"
                  >
                    Approuver
                  </SmallBtn>
                </>
              )}
            </div>
          </div>
        </div>
      </button>
    );
  }

  return (
    <button
      onClick={onOpen}
      className="w-full text-left rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-4 py-3 hover:shadow-sm transition"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 dark:text-white">
              Transfert wallet
            </span>
            <span className={statusPill.cls}>{statusPill.label}</span>
          </div>

          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 break-words">
            Ref:{" "}
            <b className="text-slate-700 dark:text-slate-200">{ref}</b> • {created}
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <MetaBlock
              label="Expéditeur"
              primary={tx.sender?.fullName || "—"}
              secondary={[
                tx.sender?.email || null,
                tx.sender?.phone || null,
                tx.sender?.id != null ? `User ID: ${tx.sender.id}` : null,
              ]}
            />

            <MetaBlock
              label="Bénéficiaire"
              primary={tx.recipient?.fullName || "—"}
              secondary={[
                tx.recipient?.email || null,
                tx.recipient?.phone || null,
                tx.recipient?.isInternalUser
                  ? tx.recipient?.id != null
                    ? `Interne • User ID: ${tx.recipient.id}`
                    : "Interne"
                  : "Externe / manuel",
              ]}
            />

            <MetaBlock
              label="Wallet expéditeur"
              primary={
                tx.senderWallet?.id != null ? `Wallet #${tx.senderWallet.id}` : "—"
              }
              secondary={[
                tx.senderWallet?.balance != null
                  ? `Solde: ${fmtMoney(
                      tx.senderWallet.balance,
                      tx.senderWallet.currency || "CAD"
                    )}`
                  : null,
                tx.senderWallet?.heldBalance != null
                  ? `Hold: ${fmtMoney(
                      tx.senderWallet.heldBalance,
                      tx.senderWallet.currency || "CAD"
                    )}`
                  : null,
              ]}
            />
          </div>

          {tx.note && (
            <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 break-words">
              Note:{" "}
              <span className="text-slate-700 dark:text-slate-200">{tx.note}</span>
            </div>
          )}
        </div>

        <div className="text-right shrink-0">
          <div className="text-sm font-semibold text-slate-900 dark:text-white whitespace-nowrap">
            {fmtMoney(tx.amount, tx.currency || "CAD")}
          </div>

          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            Fee: {fmtMoney(tx.feeAmount || 0, tx.feeCurrency || "CAD")}
          </div>

          <div className="mt-2 flex items-center justify-end gap-2">
            {tx.status === "pending" && (
              <>
                <SmallBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject();
                  }}
                  variant="ghost"
                >
                  Rejeter
                </SmallBtn>
                <SmallBtn
                  onClick={(e) => {
                    e.stopPropagation();
                    onApprove();
                  }}
                  variant="primary"
                >
                  Approuver
                </SmallBtn>
              </>
            )}
          </div>
        </div>
      </div>
    </button>
  );
}

function MetaBlock({ label, primary, secondary = [] }) {
  const lines = secondary.filter(Boolean);

  return (
    <div className="rounded-xl border border-black/5 dark:border-white/10 bg-white/70 dark:bg-slate-900/50 px-3 py-2 min-w-0">
      <div className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </div>
      <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white break-words">
        {primary || "—"}
      </div>
      {lines.length > 0 && (
        <div className="mt-1 space-y-0.5">
          {lines.map((line, idx) => (
            <div
              key={idx}
              className="text-xs text-slate-500 dark:text-slate-400 break-words"
            >
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SmallBtn({ children, onClick, variant = "ghost" }) {
  const cls =
    variant === "primary"
      ? "rounded-2xl bg-slate-900 text-white px-3 py-2 text-xs font-medium hover:opacity-95 transition dark:bg-white dark:text-slate-900"
      : "rounded-2xl border border-black/10 dark:border-white/10 bg-white/80 dark:bg-slate-900 px-3 py-2 text-xs font-medium text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition";

  return (
    <button onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

function pill(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed")
    return {
      label: "Confirmée",
      cls: "text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20",
    };
  if (s === "pending")
    return {
      label: "En attente",
      cls: "text-xs px-2 py-1 rounded-full bg-amber-500/10 text-amber-800 dark:text-amber-200 border border-amber-500/20",
    };
  if (s === "failed")
    return {
      label: "Rejetée",
      cls: "text-xs px-2 py-1 rounded-full bg-red-500/10 text-red-700 dark:text-red-300 border border-red-500/20",
    };
  if (s === "cancelled")
    return {
      label: "Annulée",
      cls: "text-xs px-2 py-1 rounded-full bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-500/20",
    };
  return {
    label: status || "—",
    cls: "text-xs px-2 py-1 rounded-full bg-slate-500/10 text-slate-700 dark:text-slate-300 border border-slate-500/20",
  };
}

function prettyType(type) {
  const t = String(type || "").toLowerCase();
  if (t === "fund") return "Recharge";
  if (t === "withdraw") return "Retrait";
  if (t === "loan") return "Prêt";
  if (t === "transfer_out") return "Transfert sortant";
  if (t === "transfer_in") return "Transfert entrant";
  return type || "—";
}

function fmtMoney(amount, currency = "CAD") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return `${amount ?? "—"} ${currency}`.trim();
  return `${n.toFixed(2)} ${String(currency || "CAD").toUpperCase()}`;
}

function Skeleton() {
  return (
    <div className="mt-4 space-y-3 animate-pulse">
      <div className="h-24 rounded-2xl bg-black/5 dark:bg-white/10" />
      <div className="h-24 rounded-2xl bg-black/5 dark:bg-white/10" />
      <div className="h-24 rounded-2xl bg-black/5 dark:bg-white/10" />
    </div>
  );
}